import React, { useMemo } from 'react';
import { Drawer, Form } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

import PDFTextFieldConfigure from './PDFTextFieldConfigure';
import PDFRectangleFieldConfigure from './PDFRectangleFieldConfigure';
import PDFImageConfigure from './PDFImageConfigure';
import PDFTableConfigure from './PDFTableConfigure';
import PDFRepeatableSectionConfigure from './PDFRepeatableSectionConfigure';

import BorderlessButton from '../../buttons/BorderlessButton';

import { toTitleCase } from '../../../helpers/helpers';

export default function PDFDesignerDrawer({
  open,
  onClose,
  onDrawOptionChanged,
  onDelete,
  field = {}, // the PDF Item
  page,
  numberOfPages,
  onPageChanged,
  onTextChanged,
  onApplyTextStyles,
  onLinkChanged,
  onOpenRepeatableSectionLayout,
  onClearRepeatableSectionLayout,
  onIsSectionHeaderChanged,
  onImageAdd,
  typeId, // Form Type
  sections = [], // Form sections
  collected = {},
  useStandardTemplate,
  useV3,
  lockedRepeatableLayoutSectionId = null,
  zIndex,
}) {
  const { type = '', id } = field;
  const body = useMemo(() => {
    switch (field.type) {
      case 'text':
        return (
          <PDFTextFieldConfigure
            key={id}
            onDrawOptionChanged={onDrawOptionChanged}
            field={field}
            page={page}
            numberOfPages={numberOfPages}
            onPageChanged={onPageChanged}
            onTextChanged={onTextChanged}
            onApplyTextStyles={onApplyTextStyles}
            onLinkChanged={onLinkChanged}
            onIsSectionHeaderChanged={onIsSectionHeaderChanged}
            sections={sections}
            collected={collected}
            typeId={typeId}
            useStandardTemplate={useStandardTemplate}
            lockedRepeatableLayoutSectionId={lockedRepeatableLayoutSectionId}
            useV3={useV3}
          />
        );
      case 'rectangle':
        return (
          <PDFRectangleFieldConfigure
            onDrawOptionChanged={onDrawOptionChanged}
            field={field}
            page={page}
            numberOfPages={numberOfPages}
            onPageChanged={onPageChanged}
            useV3={useV3}
          />
        );
      case 'image':
        return (
          <PDFImageConfigure
            onDrawOptionChanged={onDrawOptionChanged}
            field={field}
            page={page}
            numberOfPages={numberOfPages}
            onPageChanged={onPageChanged}
            onImageAdd={onImageAdd}
            useStandardTemplate={useStandardTemplate}
            useV3={useV3}
          />
        );
      case 'table':
        return (
          <PDFTableConfigure
            onDrawOptionChanged={onDrawOptionChanged}
            field={field}
            page={page}
            numberOfPages={numberOfPages}
            onPageChanged={onPageChanged}
            onLinkChanged={onLinkChanged}
            sections={sections}
            collected={collected}
            lockedRepeatableLayoutSectionId={lockedRepeatableLayoutSectionId}
            useV3={useV3}
          />
        );
      case 'repeatableSection':
        return (
          <PDFRepeatableSectionConfigure
            onLinkChanged={onLinkChanged}
            onOpenRepeatableSectionLayout={onOpenRepeatableSectionLayout}
            onClearRepeatableSectionLayout={onClearRepeatableSectionLayout}
            field={field}
            sections={sections}
          />
        );
      default:
        return null;
    }
  }, [
    field,
    page,
    numberOfPages,
    onPageChanged,
    onTextChanged,
    onLinkChanged,
    onOpenRepeatableSectionLayout,
    onClearRepeatableSectionLayout,
    onImageAdd,
    onDrawOptionChanged,
    sections,
    collected,
    useStandardTemplate,
    lockedRepeatableLayoutSectionId,
  ]);

  const title = useMemo(() => {
    if (type === 'repeatableSection') return 'Repeatable Section';
    return toTitleCase(type);
  }, [type]);

  return (
    <Drawer
      title={title}
      placement="right"
      visible={open}
      onClose={onClose}
      mask={false}
      width={375}
      zIndex={zIndex}
    >
      <div className="pdf-designer-drawer-container">
        <Form layout="vertical" style={{ marginTop: 10, width: '100%' }}>
          {body}
        </Form>
      </div>
      {field.deletable && (
        <div className="pdf-designer-drawer-delete">
          <BorderlessButton
            onClick={onDelete}
            iconNode={
              <DeleteOutlined style={{ color: 'red', marginLeft: 0 }} />
            }
          />
        </div>
      )}
    </Drawer>
  );
}

/* eslint-disable react/forbid-prop-types */
PDFDesignerDrawer.propTypes = {
  lockedRepeatableLayoutSectionId: PropTypes.string,
  onOpenRepeatableSectionLayout: PropTypes.func,
  onClearRepeatableSectionLayout: PropTypes.func,
  useV3: PropTypes.bool,
  field: PropTypes.object,
  collected: PropTypes.object,
  useStandardTemplate: PropTypes.bool,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  sections: PropTypes.array,
  zIndex: PropTypes.number,
};

PDFDesignerDrawer.defaultProps = {
  lockedRepeatableLayoutSectionId: null,
  onOpenRepeatableSectionLayout: () => {},
  onClearRepeatableSectionLayout: () => {},
  useV3: false,
  field: {},
  collected: {},
  useStandardTemplate: false,
  open: false,
  sections: [],
  zIndex: undefined,
};
